import React, { useEffect, useRef, useState } from 'react';
import { BTN } from '../constants/buttons';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../constants/overlayLayers';

/**
 * Strong-warning confirmation modal for deleting an entire Risk Assessment
 * (whole version chain). Requires the user to type "DELETE" to enable the
 * confirm button — defends against muscle-memory clicks on a destructive,
 * regulated-document action.
 *
 * 🚨 THE SENTENCE IN THIS MODAL IS THE CONSENT. `onConfirm` fires
 * `DELETE /api/risk-assessments/:id/chain`, which walks the chain in both
 * directions and soft-deletes EVERY version it finds — unconditionally. It
 * never reads a count from the client and there is no narrower server
 * behaviour a count could select. The blast radius is therefore FIXED, and the
 * number is only a description of it.
 *
 * 🔑 `versionCount` IS NULLABLE, AND NULL MEANS "WE DO NOT KNOW" — NEVER 1.
 * It defaulted to `1` until 2026-08-22, and the caller passed a literal `1`
 * whenever its pre-flight chain lookup failed. `1` is not a neutral fallback
 * here: it is precisely the value that DELETES the "and all N versions in its
 * chain" clause below, so a dropped connection quietly narrowed the stated
 * scope of an irreversible action while the action itself stayed whole-chain.
 * Measured, healthy vs failed: "…delete 'Working at Height — Stage 4' and all
 * 4 versions in its chain." became "…delete 'Working at Height — Stage 4'."
 * Consent obtained for less than what happens is the defect.
 *
 * On null the copy states the whole chain goes — "and every version in its
 * chain", which is true whether the chain holds one version or nine — and,
 * once `counting` is false, a notice says the number could not be checked and
 * offers a Retry. The delete is deliberately NOT blocked; see the reasoning at
 * `handleDeleteRequest` in `pages/RiskAssessmentOverview.js`.
 *
 * Props:
 *  - open: bool
 *  - raTitle: string — the RA's title (shown in the warning)
 *  - versionCount: number | null — chain length, or null when unknown
 *  - counting: bool — the chain-length lookup is still in flight
 *  - onRetryCount(): re-run the chain-length lookup
 *  - busy: bool
 *  - onCancel(): close
 *  - onConfirm(): user typed DELETE and clicked the red button
 */
export default function RaDeleteConfirmModal({
  open,
  raTitle,
  versionCount = null,
  counting = false,
  onRetryCount,
  busy = false,
  onCancel,
  onConfirm,
}) {
  const [typed, setTyped] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTyped('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  if (!open) return null;

  const armed = typed.trim().toUpperCase() === 'DELETE';
  // A count we can stand behind. Anything else — null, NaN, a numeric string,
  // a zero — is UNKNOWN, and unknown must never render as a specific number
  // nor silently collapse into the single-version wording.
  const knownCount = Number.isFinite(versionCount) && versionCount > 0 ? versionCount : null;
  // We could not find out, and we have stopped trying. `counting` is the third
  // state: not known yet, but with no failure to report either.
  const countUnknown = knownCount === null && !counting;
  const submit = (e) => {
    e.preventDefault();
    if (!armed || busy) return;
    onConfirm?.();
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/50 ${MODAL_OVERLAY}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={submit}
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-5 border-t-4 border-red-600 ${MODAL_PANEL_MOBILE_CAP}`}
        data-testid="ra-delete-confirm-modal"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="shrink-0 h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center text-red-600 dark:text-red-300 text-xl">
            ⚠
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete this RA?</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              You're about to permanently delete{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">"{raTitle}"</span>
              {knownCount !== null
                ? knownCount > 1 && (
                  <> and all <span className="font-medium">{knownCount} versions</span> in its chain</>
                )
                : (
                  // Unknown, or still arriving. The delete takes the whole chain
                  // either way, so say so — true for a one-version chain too,
                  // and it is the fact being consented to.
                  <> and <span className="font-medium">every version in its chain</span></>
                )}
              .
            </p>
            {counting && (
              <p
                className="text-xs text-gray-500 dark:text-gray-400 mt-1"
                data-testid="ra-delete-version-count-checking"
              >
                Checking how many versions are in this chain&hellip;
              </p>
            )}
            {countUnknown && (
              <p
                className="text-xs text-amber-700 dark:text-amber-300 mt-1"
                data-testid="ra-delete-version-count-unknown"
              >
                We couldn&rsquo;t check how many versions this chain holds — the
                whole chain is deleted either way.
                {onRetryCount && (
                  <>
                    {' '}
                    <button
                      type="button"
                      onClick={onRetryCount}
                      disabled={busy}
                      className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-60"
                      data-testid="ra-delete-version-count-retry"
                    >
                      Retry
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1.5 mb-4 pl-4 list-disc">
          <li>All draft work — hazard rows, controls, scoring, notes — will be lost.</li>
          <li>Signed-off versions become inaccessible (audit trail still recoverable by an admin).</li>
          <li>This cannot be undone from the UI.</li>
        </ul>

        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
            Type <span className="font-mono text-red-600 dark:text-red-400">DELETE</span> to confirm
          </span>
          <input
            ref={inputRef}
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={busy}
            maxLength={20}
            autoComplete="off"
            spellCheck={false}
            className="block w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm font-mono text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-red-500 focus:border-red-500"
            data-testid="ra-delete-confirm-input"
          />
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={BTN.secondary}
            data-testid="ra-delete-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!armed || busy}
            className={BTN.danger}
            data-testid="ra-delete-confirm"
          >
            {busy ? 'Deleting…' : 'Delete RA'}
          </button>
        </div>
      </form>
    </div>
  );
}
